import React, { useEffect, useState } from 'react';
import api, { extractApiError, isRequestCanceled, unwrapApiResponse } from '../api/api';
import { formatDuration } from '../utils/duration';
import { useToast } from './ToastProvider';
import JobDetailPanel from './JobDetailPanel';

function StatCard({ label, value }) {
  return (
    <article className="table-card stat-card">
      <span className="section-subtitle">{label}</span>
      <strong className="stat-value">{value}</strong>
    </article>
  );
}

export default function OperationsMonitorView({ getToken }) {
  const { showError, showSuccess } = useToast();
  const [status, setStatus] = useState({ loading: true });
  const [monitor, setMonitor] = useState(null);
  const [selectedJobId, setSelectedJobId] = useState(null);

  useEffect(() => {
    let timeoutId;
    let activeController;
    let alive = true;

    async function loadMonitor() {
      activeController?.abort?.();
      activeController = new AbortController();
      try {
        const token = await getToken({ skipCache: true });
        const response = await api.get('/ingestion/monitor', {
          headers: { Authorization: `Bearer ${token}` },
          signal: activeController.signal
        });
        if (!alive) return;
        setMonitor(unwrapApiResponse(response));
        setStatus({ loading: false });
      } catch (error) {
        if (!alive || isRequestCanceled(error)) return;
        setStatus({ loading: false });
        showError(extractApiError(error, 'No se pudo cargar el monitoreo operativo'));
      } finally {
        if (alive) {
          timeoutId = setTimeout(loadMonitor, 2000);
        }
      }
    }

    loadMonitor();
    return () => {
      alive = false;
      activeController?.abort?.();
      clearTimeout(timeoutId);
    };
  }, [getToken, showError]);

  async function handleCancel(job) {
    try {
      const token = await getToken({ skipCache: true });
      const response = await api.post(`/ingestion/jobs/${job.id}/cancel`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const payload = unwrapApiResponse(response);
      const monitorResponse = await api.get('/ingestion/monitor', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMonitor(unwrapApiResponse(monitorResponse));
      showSuccess(payload?.latestMessage || `Se canceló ${job.name} y se reinició su información`, { duration: 3500 });
    } catch (error) {
      if (isRequestCanceled(error)) return;
      showError(extractApiError(error, 'No se pudo cancelar el job'));
    }
  }

  return (
    <section className="monitor-layout">
      <div className="monitor-stats-grid">
        <StatCard label="Jobs totales" value={monitor?.totalJobs || 0} />
        <StatCard label="En cola" value={monitor?.queuedJobs || 0} />
        <StatCard label="Procesando" value={(monitor?.processingJobs || 0) + (monitor?.cancelingJobs || 0)} />
        <StatCard label="Completados" value={monitor?.completedJobs || 0} />
        <StatCard label="Fallidos" value={monitor?.failedJobs || 0} />
        <StatCard label="Cancelados" value={monitor?.canceledJobs || 0} />
      </div>

      {status.loading ? <p className="section-subtitle">Cargando monitoreo...</p> : null}

      <section className="table-card">
        <div className="table-toolbar compact-toolbar mobile-stack-toolbar">
          <div>
            <div className="brand-badge">Operación</div>
            <h2 className="section-title with-margin">Monitoreo operativo</h2>
            <p className="section-subtitle">Resumen de actividad y estado reciente de la ingesta</p>
          </div>
        </div>

        <div className="table-shell report-table-shell">
          <table className="report-table responsive-job-table">
            <thead>
              <tr>
                <th>Job</th>
                <th>Estado</th>
                <th>Config</th>
                <th>Entidad</th>
                <th>Archivos</th>
                <th>Aceptadas</th>
                <th>Rechazadas</th>
                <th>Duración</th>
                <th>Acciones</th>
                <th>Mensaje</th>
              </tr>
            </thead>
            <tbody>
              {monitor?.recentJobs?.length ? monitor.recentJobs.map((job) => (
                <tr key={job.id}>
                  <td>{job.name}</td>
                  <td><span className={`status-pill ${job.status}`}>{job.status}</span></td>
                  <td>{job.configName}</td>
                  <td>{job.tenantId || '-'}</td>
                  <td>{job.successFiles || 0}/{job.totalFiles || job.uploadedFiles || 0}</td>
                  <td>{Number(job.insertedRows || 0)}</td>
                  <td>{Number(job.rejectedRows || 0)}</td>
                  <td>{job.durationLabel || formatDuration(job.durationMs)}</td>
                  <td>
                    <div className="row-action-group">
                      <button type="button" className="secondary-button small-action-button" onClick={() => setSelectedJobId(job.id)}>Detalle</button>
                      <button type="button" className="secondary-button small-action-button" onClick={() => handleCancel(job)} disabled={!job.canCancel}>Cancelar</button>
                    </div>
                  </td>
                  <td>{job.latestMessage || '-'}</td>
                </tr>
              )) : (
                <tr><td colSpan="10" className="empty-cell">Sin actividad reciente</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <JobDetailPanel jobId={selectedJobId} getToken={getToken} onClose={() => setSelectedJobId(null)} />
    </section>
  );
}
